import React, { useState, useEffect } from 'react';
import { HiOutlineCurrencyDollar, HiOutlineWallet, HiOutlineClock, HiOutlineChevronLeft, HiOutlineChevronRight, HiOutlineBanknotes, HiOutlineXMark, HiOutlineInformationCircle } from 'react-icons/hi2';
import { GiSailboat } from 'react-icons/gi';
import { MdOutlineExplore } from 'react-icons/md';

import { db } from '../../config/firebaseConfig';
import { getDocs, collection, query, where, addDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';

import dayjs from 'dayjs';

const EarningsReport = () => {
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deductionPage, setDeductionPage] = useState(0);
  const [deductionRowsPerPage, setDeductionRowsPerPage] = useState(5);
  const [deductions, setDeductions] = useState([]);
  const [earnings, setEarnings] = useState({
    summary: {
      completed: 0,
      upcoming: 0,
      balance: 0
    },
    transactions: []
  });
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [withdrawSubmitting, setWithdrawSubmitting] = useState(false);
  const [withdrawSuccess, setWithdrawSuccess] = useState(false);
  const [withdrawRequests, setWithdrawRequests] = useState([]);
  const [withdrawPage, setWithdrawPage] = useState(0);
  const [withdrawRowsPerPage, setWithdrawRowsPerPage] = useState(5);
  const [billingInfoModalOpen, setBillingInfoModalOpen] = useState(false);

  const { currentUser, userData } = useAuth();
  const now = dayjs();

  const billingType = userData?.billingType || userData?.affiliateApplication?.billingType || '';
  const payoutMultiplier = billingType === 'turkish_company' ? 1.0 : billingType === 'turkish_citizen' ? 0.7 : billingType === 'foreign_company' ? 0.65 : 0;
  const payoutAmount = earnings.summary.balance * payoutMultiplier;

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        // Fetch yacht bookings
        const bookingsCollection = collection(db, 'bookings');
        const bookingsQuery = query(bookingsCollection, where('affiliateId', '==', currentUser.uid));
        const bookingsSnapshot = await getDocs(bookingsQuery);
        const yachtBookings = bookingsSnapshot.docs.map(doc => ({
          id: doc.id,
          type: 'yacht',
          ...doc.data()
        }));

        // Fetch experience bookings
        const expBookingsCollection = collection(db, 'experienceBookings');
        const expBookingsQuery = query(expBookingsCollection, where('affiliateId', '==', currentUser.uid));
        const expBookingsSnapshot = await getDocs(expBookingsQuery);
        const experienceBookings = expBookingsSnapshot.docs
          .map(doc => ({
            id: doc.id,
            type: 'experience',
            ...doc.data()
          }))
          .filter(booking => booking.paymentStatus !== 'pending' && booking.paymentStatus !== 'failed' && booking.status !== 'cancelled');
        
        // Combine all bookings
        const allBookings = [...yachtBookings, ...experienceBookings];
        
        // Fetch balance deductions
        const deductionsCollection = collection(db, 'balanceDeductions');
        const deductionsQuery = query(deductionsCollection, where('affiliateId', '==', currentUser.uid));
        const deductionsSnapshot = await getDocs(deductionsQuery);
        const deductionsList = deductionsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || new Date()
        })).sort((a, b) => b.createdAt - a.createdAt);
        const totalDeductions = deductionsList
          .reduce((sum, d) => sum + (d.amount || 0), 0);
        setDeductions(deductionsList);

        // Calculate combined summary
        const rawBalance = allBookings
          .filter(booking => booking.date < now.format('YYYY-MM-DD') && !booking.affiliatePaid)
          .reduce((sum, booking) => sum + (booking.commission || 0), 0);

        const summary = {
          completed: allBookings
            .filter(booking => booking.date < now.format('YYYY-MM-DD') && booking.affiliatePaid === true)
            .reduce((sum, booking) => sum + (booking.commission || 0), 0),
          upcoming: allBookings
            .filter(booking => booking.date >= now.format('YYYY-MM-DD'))
            .reduce((sum, booking) => sum + (booking.commission || 0), 0),
          balance: Math.max(0, rawBalance - totalDeductions),
        };

        // Fetch withdraw requests
        const withdrawCollection = collection(db, 'withdrawRequests');
        const withdrawQuery = query(withdrawCollection, where('affiliateId', '==', currentUser.uid));
        const withdrawSnapshot = await getDocs(withdrawQuery);
        const withdrawList = withdrawSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || new Date()
        })).sort((a, b) => b.createdAt - a.createdAt);
        setWithdrawRequests(withdrawList);

        // Deduct pending/completed withdraw request amounts from displayed balance
        const totalWithdrawn = withdrawList.reduce((sum, req) => sum + (req.balanceAmount || 0), 0);
        setEarnings({
          summary: { ...summary, balance: Math.max(0, summary.balance - totalWithdrawn) },
          transactions: allBookings.sort((a, b) => new Date(b.date) - new Date(a.date))
        });

        setLoading(false);
      } catch (error) {
        console.error('Error fetching bookings:', error);
        setLoading(false);
      }
    };
    fetchBookings();
  }, []);

  const filteredTransactions = earnings.transactions;
  const totalPages = Math.ceil(filteredTransactions.length / rowsPerPage);

  const getStatus = (transaction) => {
    if (transaction.date < now.format('YYYY-MM-DD') && transaction.affiliatePaid === true) {
      return 'completed';
    } else if (transaction.date >= now.format('YYYY-MM-DD')) {
      return 'upcoming';
    }
    return 'balance';
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'upcoming': return 'Upcoming';
      case 'balance': return 'Balance';
      default: return status;
    }
  };

  const handleWithdrawRequest = async () => {
    if (earnings.summary.balance <= 0) return;
    if (!billingType) {
      alert('Please select your billing status from the Payment section in your Profile page before requesting a withdrawal.');
      return;
    }
    setWithdrawSubmitting(true);
    try {
      await addDoc(collection(db, 'withdrawRequests'), {
        affiliateId: currentUser.uid,
        affiliateEmail: currentUser.email,
        affiliateName: userData?.firstName && userData?.lastName ? `${userData.firstName} ${userData.lastName}` : currentUser.email,
        balanceAmount: earnings.summary.balance,
        payoutAmount: payoutAmount,
        billingType: billingType,
        status: 'pending',
        createdAt: serverTimestamp()
      });
      setWithdrawSuccess(true);
    } catch (error) {
      console.error('Error submitting withdraw request:', error);
    } finally {
      setWithdrawSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="section-header">
        <div>
          <h2 className="section-title">Earnings Report</h2>
          <p className="section-subtitle">Track your commission earnings from successful referrals.</p>
        </div>
      </div>

      {/* Earnings Summary Cards */}
      <div className="stats-grid">
        {/* <div className="stat-card">
          <div className="stat-icon green">
            <HiOutlineCurrencyDollar />
          </div>
          <div className="stat-value">€{earnings.summary.completed.toFixed(2)}</div>
          <div className="stat-label">Completed Earnings</div>
        </div> */}
        
        <div className="stat-card">
          <div className="stat-icon orange">
            <HiOutlineClock />
          </div>
          <div className="stat-value">€{earnings.summary.upcoming.toFixed(2)}</div>
          <div className="stat-label">Upcoming Earnings</div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon blue">
            <HiOutlineWallet />
          </div>
          <div className="stat-value">€{earnings.summary.balance.toFixed(2)}</div>
          <div className="stat-label">Balance</div>
          {earnings.summary.balance > 0 && (
            <button className="withdraw-btn" onClick={() => setWithdrawModalOpen(true)} disabled={earnings.summary.balance < 100}>
              <HiOutlineBanknotes /> Request Withdrawal
            </button>
          )}
          {earnings.summary.balance > 0 && earnings.summary.balance < 100 && (
            <span className="withdraw-min-note">Minimum withdrawal amount is €100</span>
          )}
        </div>
      </div>

      {/* Transactions Table */}
      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Type</th>
              {/* <th>Booking ID</th> */}
              <th>Customer</th>
              {/* <th>Booking Amount</th> */}
              <th>Commission</th>
              <th>Date</th>
              <th>Affiliate Code</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredTransactions.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ textAlign: 'center', padding: '40px' }}>
                  No transactions yet
                </td>
              </tr>
            ) : (
              filteredTransactions
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((transaction) => {
                  const bookingAmount = transaction.priceBreakdown?.finalPrice || transaction.priceBreakdown?.basePrice || 0;
                  const status = getStatus(transaction);
                  return (
                    <tr key={transaction.id}>
                      <td>
                        <span className={`type-badge ${transaction.type}`}>
                          {transaction.type === 'yacht' ? <GiSailboat /> : <MdOutlineExplore />}
                          {transaction.type === 'yacht' ? 'Yacht' : 'Experience'}
                        </span>
                      </td>
                      {/* <td>{transaction.id.substring(0, 8).toUpperCase()}</td> */}
                      <td>{transaction.customerName || transaction.userEmail || 'N/A'}</td>
                      {/* <td>€{bookingAmount.toFixed(2)}</td> */}
                      <td>€{(transaction.commission || 0).toFixed(2)}</td>
                      <td>{transaction.date}</td>
                      <td>
                        <span className="code-text">{transaction.discountCode || 'N/A'}</span>
                      </td>
                      <td>
                        <span className={`status-badge ${status}`}>
                          {getStatusLabel(status)}
                        </span>
                      </td>
                    </tr>
                  );
                })
            )}
          </tbody>
        </table>
        
        {filteredTransactions.length > 0 && (
          <div className="table-pagination">
            <div className="pagination-info">
              Showing {page * rowsPerPage + 1} to {Math.min((page + 1) * rowsPerPage, filteredTransactions.length)} of {filteredTransactions.length} entries
            </div>
            <div className="pagination-controls">
              <select 
                className="rows-select" 
                value={rowsPerPage} 
                onChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
              </select>
              <button 
                className="pagination-btn" 
                onClick={() => setPage(page - 1)} 
                disabled={page === 0}
              >
                <HiOutlineChevronLeft />
              </button>
              <button 
                className="pagination-btn" 
                onClick={() => setPage(page + 1)} 
                disabled={page >= totalPages - 1}
              >
                <HiOutlineChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>
      {/* Balance Usage History Table */}
      <div className="section-header" style={{ marginTop: '40px' }}>
        <div>
          <h2 className="section-title">Balance Usage History</h2>
          <p className="section-subtitle">Bookings where you used your commission balance as payment.</p>
        </div>
      </div>

      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Experience</th>
              <th>Amount Used</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {deductions.length === 0 ? (
              <tr>
                <td colSpan="3" style={{ textAlign: 'center', padding: '40px' }}>
                  No balance usage yet
                </td>
              </tr>
            ) : (
              deductions
                .slice(deductionPage * deductionRowsPerPage, deductionPage * deductionRowsPerPage + deductionRowsPerPage)
                .map((deduction) => (
                  <tr key={deduction.id}>
                    <td>{deduction.experienceName || deduction.description || 'N/A'}</td>
                    <td style={{ color: '#ef4444', fontWeight: 600 }}>-€{(deduction.amount || 0).toFixed(2)}</td>
                    <td>{deduction.createdAt ? dayjs(deduction.createdAt).format('YYYY-MM-DD HH:mm') : 'N/A'}</td>
                  </tr>
                ))
            )}
          </tbody>
        </table>

        {deductions.length > 0 && (
          <div className="table-pagination">
            <div className="pagination-info">
              Showing {deductionPage * deductionRowsPerPage + 1} to {Math.min((deductionPage + 1) * deductionRowsPerPage, deductions.length)} of {deductions.length} entries
            </div>
            <div className="pagination-controls">
              <select
                className="rows-select"
                value={deductionRowsPerPage}
                onChange={(e) => { setDeductionRowsPerPage(parseInt(e.target.value, 10)); setDeductionPage(0); }}
              >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
              </select>
              <button
                className="pagination-btn"
                onClick={() => setDeductionPage(deductionPage - 1)}
                disabled={deductionPage === 0}
              >
                <HiOutlineChevronLeft />
              </button>
              <button
                className="pagination-btn"
                onClick={() => setDeductionPage(deductionPage + 1)}
                disabled={deductionPage >= Math.ceil(deductions.length / deductionRowsPerPage) - 1}
              >
                <HiOutlineChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>
      {/* Withdraw Requests Table */}
      <div className="section-header" style={{ marginTop: '40px' }}>
        <div>
          <h2 className="section-title">Withdraw Requests</h2>
          <p className="section-subtitle">Track the status of your withdrawal requests.</p>
        </div>
        <button className="btn-secondary" onClick={() => setBillingInfoModalOpen(true)}>
          <HiOutlineInformationCircle /> Billing Info
        </button>
      </div>

      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Balance Amount</th>
              <th>Payout Amount (After TAX)</th>
              {/* <th>Billing Type</th> */}
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {withdrawRequests.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center', padding: '40px' }}>
                  No withdrawal requests yet
                </td>
              </tr>
            ) : (
              withdrawRequests
                .slice(withdrawPage * withdrawRowsPerPage, withdrawPage * withdrawRowsPerPage + withdrawRowsPerPage)
                .map((request) => (
                  <tr key={request.id}>
                    <td>{request.createdAt ? dayjs(request.createdAt).format('YYYY-MM-DD HH:mm') : 'N/A'}</td>
                    <td>€{(request.balanceAmount || 0).toFixed(2)}</td>
                    <td style={{ color: 'var(--color-accent-teal)', fontWeight: 600 }}>€{(request.payoutAmount || 0).toFixed(2)}</td>
                    {/* <td>{request.billingType === 'turkish_company' ? 'Turkish Company' : request.billingType === 'turkish_citizen' ? 'Turkish Citizen' : request.billingType === 'foreign_company' ? 'Foreign Company' : 'N/A'}</td> */}
                    <td>
                      <span className={`status-badge ${request.status}`}>
                        {request.status === 'pending' ? 'Pending' : request.status === 'completed' ? 'Completed' : request.status === 'rejected' ? 'Rejected' : request.status}
                      </span>
                    </td>
                  </tr>
                ))
            )}
          </tbody>
        </table>

        {withdrawRequests.length > 0 && (
          <div className="table-pagination">
            <div className="pagination-info">
              Showing {withdrawPage * withdrawRowsPerPage + 1} to {Math.min((withdrawPage + 1) * withdrawRowsPerPage, withdrawRequests.length)} of {withdrawRequests.length} entries
            </div>
            <div className="pagination-controls">
              <select
                className="rows-select"
                value={withdrawRowsPerPage}
                onChange={(e) => { setWithdrawRowsPerPage(parseInt(e.target.value, 10)); setWithdrawPage(0); }}
              >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
              </select>
              <button
                className="pagination-btn"
                onClick={() => setWithdrawPage(withdrawPage - 1)}
                disabled={withdrawPage === 0}
              >
                <HiOutlineChevronLeft />
              </button>
              <button
                className="pagination-btn"
                onClick={() => setWithdrawPage(withdrawPage + 1)}
                disabled={withdrawPage >= Math.ceil(withdrawRequests.length / withdrawRowsPerPage) - 1}
              >
                <HiOutlineChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Billing Info Modal */}
      {billingInfoModalOpen && (
        <div className="withdraw-modal-overlay" onClick={() => setBillingInfoModalOpen(false)}>
          <div className="withdraw-modal" onClick={(e) => e.stopPropagation()}>
            <button className="withdraw-modal-close" onClick={() => setBillingInfoModalOpen(false)}>
              <HiOutlineXMark />
            </button>
            <h3 className="withdraw-modal-title"><HiOutlineInformationCircle /> Company Billing Info</h3>
            <div className="withdraw-modal-info">
              <p>Please address your invoices to the following company:</p>
              <div className="withdraw-company-details">
                <p><strong>GEN TRAVEL TURİZM ORGANİZASYON VE SAĞLIK HİZMETLERİ ANONİM ŞİRKETİ</strong></p>
                <p>VD: Başakşehir</p>
                <p>Vergi No: 3910642570</p>
              </div>
              <p style={{ marginBottom: 0 }}>Send your invoice to: <strong>simon@bosphorusyachts.com</strong></p>
            </div>
          </div>
        </div>
      )}

      {/* Withdraw Modal */}
      {withdrawModalOpen && (
        <div className="withdraw-modal-overlay" onClick={() => { setWithdrawModalOpen(false); setWithdrawSuccess(false); }}>
          <div className="withdraw-modal" onClick={(e) => e.stopPropagation()}>
            <button className="withdraw-modal-close" onClick={() => { setWithdrawModalOpen(false); setWithdrawSuccess(false); }}>
              <HiOutlineXMark />
            </button>
            <h3 className="withdraw-modal-title"><HiOutlineBanknotes /> Request Withdrawal</h3>
            
            {!withdrawSuccess ? (
              <>
                <div className="withdraw-modal-info">
                  <p>Please create an invoice with the following information and send to <strong>simon@bosphorusyachts.com</strong> in order for the payout.</p>
                  
                  <div className="withdraw-company-details">
                    <p><strong>GEN TRAVEL TURİZM ORGANİZASYON VE SAĞLIK HİZMETLERİ ANONİM ŞİRKETİ</strong></p>
                    <p>VD: Başakşehir</p>
                    <p>Vergi No: 3910642570</p>
                  </div>

                  <div className="withdraw-amount">
                    <span>Your Balance:</span>
                    <strong>€{earnings.summary.balance.toFixed(2)}</strong>
                  </div>
                  <div className="withdraw-amount" style={{ marginTop: '8px' }}>
                    <span>Payout Amount ({billingType === 'turkish_company' ? '100%' : billingType === 'turkish_citizen' ? '70%' : billingType === 'foreign_company' ? '65%' : '—'}):</span>
                    <strong>€{payoutAmount.toFixed(2)}</strong>
                  </div>
                </div>

                {!billingType && (
                  <div className="withdraw-modal-note">
                    <HiOutlineInformationCircle />
                    <span>Please set your billing status in your Profile → Payment Settings to see your payout amount.</span>
                  </div>
                )}

                <div className="withdraw-modal-note">
                  <HiOutlineInformationCircle />
                  <span>Please note that only Turkish companies can receive 100% of the payout. If you are an individual or non-Turkish entity, the withdrawal request will subtract taxes and fees.</span>
                </div>

                <button 
                  className="withdraw-submit-btn" 
                  onClick={handleWithdrawRequest}
                  disabled={withdrawSubmitting}
                >
                  {withdrawSubmitting ? 'Submitting...' : 'Confirm Withdrawal Request'}
                </button>
              </>
            ) : (
              <div className="withdraw-success">
                <p>✓ Your withdrawal request has been submitted successfully!</p>
                <p>Please send your invoice to <strong>simon@bosphorusyachts.com</strong> to complete the process.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EarningsReport;
